import { format as fnsFormat, formatDistanceToNow, parseISO } from "date-fns"
import { enUS, es } from "date-fns/locale"

import type { GradeScale } from "@/constants/academic"
import i18n from "@/i18n"

const dateFnsLocale = () => (i18n.language.startsWith("es") ? es : enUS)

// `parseISO`, not `new Date`: a date-only string ("2026-09-19", a snapshot's
// Saturday) is UTC midnight to `new Date`, which Mexico City shows as the
// day before. `parseISO` reads it as a local calendar day.
const toDate = (d: Date | string) => (typeof d === "string" ? parseISO(d) : d)

export function formatDate(d: Date | string, pattern = "PP") {
  const date = toDate(d)
  return fnsFormat(date, pattern, { locale: dateFnsLocale() })
}

/** Verbose relative time ("hace 2 días" / "2 days ago"). */
export function formatDateTime(d: Date | string) {
  const date = toDate(d)
  return fnsFormat(date, "PPp", { locale: dateFnsLocale() })
}

export function formatRelative(d: Date | string) {
  const date = toDate(d)
  return formatDistanceToNow(date, { locale: dateFnsLocale(), addSuffix: true })
}

export function formatNumber(n: number, opts?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(i18n.language, opts).format(n)
}

/**
 * Format a grade on the tenant's configured scale (PRD-38 §FR-14).
 *
 * `scale` is REQUIRED on purpose: this used to hardcode 0-10, which rendered
 * every 0-100 institution wrong across the whole dashboard. Making it a
 * required parameter means `tsc` — not a code reviewer — catches the next
 * consumer that forgets to thread the resolved scale through.
 *
 * Precision follows the scale rather than a fixed digit count: one decimal is
 * how a 0-10 school writes 8.4, while a 0-100 school writes 84, not 84.0. The
 * VALUE is never converted between scales — PRD-38 §Non-goals rules that out.
 */
export function formatGrade(n: number, scale: GradeScale) {
  const digits = scale === "0-100" ? 0 : 1
  return formatNumber(n, { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/** Accepts a 0-100 percentage; formats per active locale. */
export function formatPercent(percent: number) {
  return formatNumber(percent / 100, { style: "percent", maximumFractionDigits: 0 })
}

/** "nombre, ciudad y nivel" / "name, city, and level" — never a hand-joined ", ". */
export function formatList(items: string[]) {
  return new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(items)
}
